import { type AssetMonitoringDto, incidentPath, MONITOR_SIMULATION_NOTE } from '@fos/shared/contracts/monitoring';
import {
  failThresholdText,
  MONITOR_STATE_LABELS,
  MONITOR_STATE_TONES,
  monitorIntervalLabel,
  recoverThresholdText,
  uptimeText,
} from '@fos/shared/labels';
import { ChevronRight, FlaskConical, RefreshCw } from 'lucide-react';
import { type ReactNode, type Ref, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { formatRelativePast } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { Mono } from '../../../ui/Mono';
import { Section } from '../../../ui/Section';
import { ErrorState, Skeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { toast } from '../../../ui/Toast';
import { Tooltip } from '../../../ui/Tooltip';
import { TOUCH_BUTTON, TOUCH_TEXT } from '../../mobile';
import { lastProblemText, latencyText, sinceText } from '../monitoring-format';
import { MonitorDialog } from './MonitorDialog';
import { UptimeBar } from './UptimeBar';

/** Odświeżanie sekcji (stan monitora zmienia worker co 2 min). */
const REFRESH_MS = 30_000;
/**
 * Po „Sprawdź teraz” i przełączniku symulacji worker sprawdza stronę w ciągu kilku sekund: przez 45 s sekcja odświeża
 * się co 2 s, żeby wynik (np. alarm po drugim błędzie) był widoczny bez przeładowania strony.
 */
const FAST_REFRESH_MS = 2_000;
const FAST_WINDOW_MS = 45_000;

function Card({ action, children }: { action?: ReactNode; children: ReactNode }) {
  return (
    <Section aria-label="Monitoring" className="md:px-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-caption text-fg-muted">Monitoring</h2>
        {action}
      </div>
      <div className="mt-2.5">{children}</div>
    </Section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <dt className="shrink-0 text-caption text-fg-muted">{label}</dt>
      <dd className="min-w-0 text-right text-caption break-words text-fg">{children}</dd>
    </div>
  );
}

function LinkAction({
  children,
  onClick,
  ref,
}: {
  children: ReactNode;
  onClick: () => void;
  ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={`rounded-xs text-caption font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
    >
      {children}
    </button>
  );
}

/**
 * Sekcja Monitoring w karcie zasobu (Figma 08, 3044:3978 — „Dostępność 30 dni 99,96%”, „Ostatni problem 12.09, 4 min”;
 * MODULE_UTRZYMANIE §5): stan monitora (Działa / Alarm / Sprawdzanie), otwarty incydent z linkiem, dostępność z 30 dni
 * z paskiem dni, ostatnie sprawdzenie, ostatni problem, ostatnie incydenty. Akcje: „Włącz monitoring” / „Zmień”
 * (Utrzymanie: Edycja), „Sprawdź teraz”, przełącznik symulacji „Symuluj awarię / Przywróć” (owner/admin, tryb
 * symulacji — wyraźnie oznaczony). Zmiana stanu odświeża kartę zasobu (status w nagłówku i na liście Utrzymania).
 */
export function AssetMonitoringCard({ assetId, assetName }: { assetId: string; assetName: string }) {
  const utils = trpc.useUtils();
  const fastUntil = useRef(0);
  const query = trpc.monitoring.assetMonitoring.useQuery(
    { assetId },
    { refetchInterval: () => (Date.now() < fastUntil.current ? FAST_REFRESH_MS : REFRESH_MS) },
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const actionRef = useRef<HTMLButtonElement>(null);
  /**
   * Po włączeniu albo wyłączeniu monitoringu przycisk, który otworzył okno, znika — fokus przechodzi na akcję,
   * która go zastąpiła („Zmień” albo „Włącz monitoring”).
   */
  const focusActionAfterClose = useRef(false);
  const checkNow = trpc.monitoring.checkNow.useMutation();
  const simulate = trpc.monitoring.setSimulation.useMutation();

  // status zasobu bierze się z monitora: zmiana stanu = odśwież kartę zasobu i listę Utrzymania
  const lastState = useRef<string | null>(null);
  const state = query.data?.monitor?.state ?? null;
  useEffect(() => {
    if (lastState.current !== null && state !== lastState.current) void utils.maintenance.invalidate();
    lastState.current = state;
  }, [state, utils]);

  // zapis czeka na odświeżenie danych przed zamknięciem okna, więc przy zamknięciu nowa akcja już jest w DOM
  useEffect(() => {
    if (dialogOpen || !focusActionAfterClose.current) return;
    focusActionAfterClose.current = false;
    // po fokusie, który Radix przywraca przy zamknięciu okna (setTimeout 0 w FocusScope)
    const timer = window.setTimeout(() => actionRef.current?.focus({ preventScroll: true }), 0);
    return () => window.clearTimeout(timer);
  }, [dialogOpen]);

  if (query.isPending) {
    return (
      <Card>
        <div role="status" aria-label="Wczytywanie monitoringu" className="flex flex-col gap-2">
          <Skeleton className="h-3.5 w-3/5" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-3.5 w-2/5" />
        </div>
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card>
        <ErrorState
          compact
          title="Nie udało się wczytać monitoringu"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      </Card>
    );
  }

  const data: AssetMonitoringDto = query.data;
  const { monitor, uptime, lastCheck, openIncident, lastProblem, canEdit, canManageMode, canSimulate } = data;
  const dialog = (
    <MonitorDialog
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      assetId={assetId}
      assetName={assetName}
      monitor={monitor}
      onSaved={(kind) => {
        if (kind !== 'updated') focusActionAfterClose.current = true;
      }}
      suggestedUrl={data.suggestedUrl}
      defaultMode={data.defaultMode}
      canManageMode={canManageMode}
    />
  );

  if (!monitor) {
    return (
      <Card>
        <p className="text-caption text-fg-secondary">
          Monitoring wyłączony. Po włączeniu sprawdzamy stronę co 2 min i alarmujemy po 2 błędach z rzędu.
        </p>
        {canEdit && (
          <Button
            ref={actionRef}
            variant="secondary"
            size="s"
            className={`mt-3 ${TOUCH_BUTTON}`}
            onClick={() => setDialogOpen(true)}
          >
            Włącz monitoring
          </Button>
        )}
        {data.incidents.length > 0 && <IncidentList data={data} />}
        {dialog}
      </Card>
    );
  }

  const simulated = monitor.mode === 'simulated';
  const run = async (fn: () => Promise<unknown>, message: string, description?: string) => {
    try {
      await fn();
      fastUntil.current = Date.now() + FAST_WINDOW_MS;
      toast.success(message, description ? { description } : undefined);
      await Promise.all([utils.monitoring.invalidate(), utils.maintenance.invalidate()]);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const busy = checkNow.isPending || simulate.isPending;

  return (
    <Card
      action={
        canEdit ? (
          <LinkAction ref={actionRef} onClick={() => setDialogOpen(true)}>
            Zmień
          </LinkAction>
        ) : undefined
      }
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {monitor.enabled ? (
          <StatusPill tone={MONITOR_STATE_TONES[monitor.state]}>{MONITOR_STATE_LABELS[monitor.state]}</StatusPill>
        ) : (
          <StatusPill tone="neutral">Wyłączony</StatusPill>
        )}
        <span className="text-caption text-fg-muted">{monitorIntervalLabel(monitor.intervalSec)}</span>
        {simulated && (
          <Tooltip content={MONITOR_SIMULATION_NOTE}>
            <span className="inline-flex items-center gap-1 text-caption text-fg-muted">
              <FlaskConical size={13} strokeWidth={1.75} aria-hidden />
              Symulacja
              <span className="sr-only">: {MONITOR_SIMULATION_NOTE}</span>
            </span>
          </Tooltip>
        )}
      </div>

      {openIncident && (
        <Link
          to={incidentPath(openIncident.id)}
          className="mt-2.5 flex items-center gap-2 rounded-lg bg-danger-subtle px-3 py-2 text-caption text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          <span className="min-w-0 flex-1">
            Awaria {sinceText(openIncident.openedAt)} · {openIncident.cause ?? 'strona nie odpowiada'}
          </span>
          <ChevronRight size={14} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
        </Link>
      )}

      <dl className="mt-2">
        <Row label="Dostępność 30 dni">
          <Mono>{uptimeText(uptime?.percent ?? null)}</Mono>
        </Row>
        {uptime && uptime.total > 0 && <UptimeBar uptime={uptime} className="mt-1 mb-1.5" />}
        <Row label="Ostatnie sprawdzenie">
          {lastCheck ? (
            <span className={cn(!lastCheck.ok && 'text-danger')}>
              {formatRelativePast(lastCheck.checkedAt)}
              {lastCheck.ok && latencyText(lastCheck.latencyMs) && (
                <>
                  {' · '}
                  <Mono>{latencyText(lastCheck.latencyMs)}</Mono>
                </>
              )}
            </span>
          ) : (
            <span className="text-fg-muted">jeszcze nie sprawdzono</span>
          )}
        </Row>
        {lastCheck && !lastCheck.ok && !openIncident && (
          <p className="pb-1 text-right text-caption break-words text-danger">
            {lastCheck.error ?? 'Błąd sprawdzenia'}
          </p>
        )}
        <Row label="Ostatni problem">
          {lastProblem ? (
            <Link
              to={incidentPath(lastProblem.id)}
              className="rounded-xs hover:underline focus-visible:outline-2 focus-visible:outline-accent"
            >
              {lastProblemText(lastProblem)}
            </Link>
          ) : (
            <span className="text-fg-muted">brak</span>
          )}
        </Row>
      </dl>

      {canEdit && monitor.enabled && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Button
            variant="secondary"
            size="s"
            className={TOUCH_BUTTON}
            icon={RefreshCw}
            disabled={busy}
            onClick={() =>
              run(
                () => checkNow.mutateAsync({ id: monitor.id }),
                'Sprawdzenie zlecone',
                'Wynik pojawi się w ciągu minuty.',
              )
            }
          >
            Sprawdź teraz
          </Button>
          {simulated && canSimulate && (
            <Button
              variant="ghost"
              size="s"
              className={TOUCH_BUTTON}
              disabled={busy}
              onClick={() =>
                monitor.simulatedState === 'down'
                  ? run(
                      () => simulate.mutateAsync({ id: monitor.id, state: 'up' }),
                      'Symulacja: strona działa',
                      openIncident
                        ? `Incydent zamknie się ${recoverThresholdText(monitor.recoverThreshold)}.`
                        : undefined,
                    )
                  : run(
                      () => simulate.mutateAsync({ id: monitor.id, state: 'down' }),
                      'Symulacja awarii włączona',
                      `Alarm ${failThresholdText(monitor.failThreshold)}.`,
                    )
              }
            >
              {monitor.simulatedState === 'down' ? 'Przywróć' : 'Symuluj awarię'}
            </Button>
          )}
        </div>
      )}

      {data.incidents.length > 0 && <IncidentList data={data} />}
      {dialog}
    </Card>
  );
}

/** Wcześniejsze incydenty — bez tych, które sekcja już pokazuje (trwająca awaria, „Ostatni problem”). */
function IncidentList({ data }: { data: AssetMonitoringDto }) {
  const shown = new Set([data.openIncident?.id, data.monitor ? data.lastProblem?.id : undefined]);
  const items = data.incidents.filter((i) => !shown.has(i.id)).slice(0, 3);
  if (items.length === 0) return null;
  return (
    <>
      <h3 className="mt-3 border-t border-line pt-2.5 text-caption text-fg-muted">
        {data.monitor ? 'Wcześniejsze incydenty' : 'Incydenty'}
      </h3>
      <ul className="mt-1.5 flex flex-col gap-1.5">
        {items.map((i) => (
          <li key={i.id}>
            <Link
              to={incidentPath(i.id)}
              className="flex items-center gap-2 rounded-xs text-caption hover:underline focus-visible:outline-2 focus-visible:outline-accent"
            >
              <span
                className={cn('min-w-0 flex-1 truncate', i.status === 'open' ? 'text-danger' : 'text-fg-secondary')}
              >
                {lastProblemText(i)}
                {i.status === 'open' ? ' · trwa' : ''}
              </span>
              {i.simulated && <span className="shrink-0 text-fg-muted">symulacja</span>}
            </Link>
          </li>
        ))}
      </ul>
    </>
  );
}
